/**
 * The three non-chart widget bodies plus the placeholder card
 * (`specs/M10-D2-widgets.md` §5.2–§5.5).
 *
 * They are plain DOM on purpose: a battery is a row of `<div>`s whose widths are the counts, so
 * a geometry check reads the proportions straight off the elements rather than off a canvas.
 */
import type {
  BatteryWidgetSettings,
  NumbersWidgetSettings,
  TextWidgetSettings,
} from '@elizmonday/shared';
import type { WidgetData } from '../types';
import { formatMondayNumber } from '@elizmonday/ui';
import { seriesColor } from './palette';

const AGGREGATION_CAPTIONS: Record<string, string> = {
  count: 'Items',
  count_filled: 'Filled',
  count_empty: 'Empty',
  sum: 'Sum',
  avg: 'Average',
  min: 'Minimum',
  max: 'Maximum',
  median: 'Median',
};

function formatNumber(value: number | null): string {
  if (value === null) return '—';
  if (Number.isInteger(value)) return formatMondayNumber(value);
  return formatMondayNumber(value, { maximumFractionDigits: 2 });
}

export function NumbersWidget({
  settings,
  data,
}: {
  settings: NumbersWidgetSettings;
  data: WidgetData | undefined;
}) {
  const bucket = data?.buckets[0];
  const value = bucket?.values['value'] ?? (data === undefined ? null : 0);
  const caption = AGGREGATION_CAPTIONS[settings.func] ?? settings.func;
  return (
    <div className="ez-widget-scalar" data-testid="widget-numbers">
      <div className="ez-widget-scalar__value" data-testid="widget-numbers-value">
        {formatNumber(value)}
        {settings.unit === null ? null : <span> {settings.unit}</span>}
      </div>
      <div className="ez-widget-scalar__caption">{caption}</div>
    </div>
  );
}

export function BatteryWidget({
  settings,
  data,
}: {
  settings: BatteryWidgetSettings;
  data: WidgetData | undefined;
}) {
  const buckets = data?.buckets ?? [];
  const total = buckets.reduce((sum, bucket) => sum + bucket.itemCount, 0);
  if (total === 0) {
    return (
      <div className="ez-widget-empty" data-testid="widget-no-data">
        No items to measure yet.
      </div>
    );
  }
  return (
    <div className="ez-widget-battery" data-testid="widget-battery">
      <div className="ez-widget-battery__bar" data-testid="widget-battery-bar">
        {buckets.map((bucket, index) => {
          const share = (bucket.itemCount / total) * 100;
          return (
            <div
              key={bucket.key}
              className="ez-widget-battery__segment"
              data-testid="widget-battery-segment"
              data-bucket={bucket.key}
              style={{ width: `${share}%`, background: bucket.color ?? seriesColor(index) }}
              title={`${bucket.label}: ${bucket.itemCount}`}
            >
              {settings.showPercentage && share >= 12 ? `${Math.round(share)}%` : null}
            </div>
          );
        })}
      </div>
      <div className="ez-widget-legend">
        {buckets.map((bucket, index) => (
          <span className="ez-widget-legend__entry" key={bucket.key}>
            <span
              className="ez-widget-legend__dot"
              style={{ background: bucket.color ?? seriesColor(index) }}
            />
            {bucket.label} · {bucket.itemCount}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The text widget renders its stored HTML. It is sanitised on write, and the render escapes
 * anything that is not one of the handful of inline tags the editor produces — a widget is a
 * shared surface, so a stored `<script>` would be a stored XSS.
 */
const ALLOWED_TAGS = /<(?!\/?(?:b|i|em|strong|u|br|p|ul|ol|li|h1|h2|h3|a)\b)[^>]*>/gi;

export function TextWidget({ settings }: { settings: TextWidgetSettings }) {
  const html = settings.html.replace(ALLOWED_TAGS, '').replace(/\son\w+="[^"]*"/gi, '');
  if (html.trim() === '') {
    return (
      <div className="ez-widget-empty" data-testid="widget-text-empty">
        Nothing written here yet.
      </div>
    );
  }
  return (
    <div className="ez-widget-text" data-testid="widget-text" dangerouslySetInnerHTML={{ __html: html }} />
  );
}

/** A kind we have not built, and the app widgets we cannot host yet (§5.5). */
export function WidgetPlaceholder({ title, plannedIn }: { title: string; plannedIn: string | null }) {
  return (
    <div className="ez-widget-placeholder" data-testid="widget-placeholder" data-kind={title}>
      <span className="ez-widget-placeholder__title">{title}</span>
      <span className="ez-widget-placeholder__hint">
        {plannedIn === null
          ? 'This widget is rendered by an app. Its configuration is stored and nothing is lost.'
          : `${title} widgets land with ${plannedIn}. Their configuration is already stored.`}
      </span>
    </div>
  );
}
